import { askConfirm } from './dialogs';
/**
 * collectors-admin.ts — Etapa 2 colector
 *
 * Admin UI pentru:
 *  - Listă chei API cu status live (heartbeat colector + putere)
 *  - Power Gating per linie (enable, threshold, meter deciziv)
 *  - Build & download bundle colectoare (zip)
 *  - Card "Status linii" pe dashboard (autorefresh la 10s)
 */

declare const fetch: any;

interface ApiKeyRow {
    id: number;
    name: string;
    machine_filter: string;
    last_used: string | null;
    active: number;
}

interface LineStatusRow {
    id: string;
    product: string;
    group_type: string;
    icon: string | null;
    sort_order: number | null;
    power_gating_enabled: boolean;
    power_threshold_kw: number | null;
    power_decision_machine_id: string | null;
    collector_status: 'green' | 'yellow' | 'red' | 'unknown';
    collector_last_seen: string | null;
    collector_hb_age_sec: number | null;
    last_sensor_reading_at: string | null;
    data_age_sec: number | null;
    power_kw: number | null;
    power_at: string | null;
    power_meter_id: string | null;
    running_state: 'running' | 'stopped' | 'unknown';
}

interface MachineRow { id: string; name?: string; line?: string }

const STATUS_DOTS: Record<string, string> = {
    green: '🟢',
    yellow: '🟡',
    red: '🔴',
    unknown: '⚪',
};

function _esc(s: any): string {
    return String(s ?? '').replace(/[&<>"']/g, c => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c] || c));
}

function _ago(iso: string | null): string {
    if (!iso) return '—';
    // Normalize "YYYY-MM-DDTHH:MM:SSZ" or "YYYY-MM-DD HH:MM:SS" to UTC
    let s = iso.replace(' ', 'T');
    if (!s.endsWith('Z') && !s.match(/[+-]\d{2}:?\d{2}$/)) s += 'Z';
    const t = Date.parse(s);
    if (isNaN(t)) return iso;
    const diff = Math.max(0, (Date.now() - t) / 1000);
    if (diff < 60) return `${Math.floor(diff)}s în urmă`;
    if (diff < 3600) return `${Math.floor(diff / 60)}min în urmă`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h în urmă`;
    return `${Math.floor(diff / 86400)}z în urmă`;
}

function _humanSec(sec: number): string {
    if (sec < 60) return `${sec}s`;
    if (sec < 3600) return `${Math.floor(sec / 60)}min`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
    return `${Math.floor(sec / 86400)}z`;
}

let _machinesCache: MachineRow[] = [];

async function _ensureMachinesCache(): Promise<MachineRow[]> {
    if (_machinesCache.length > 0) return _machinesCache;
    try {
        const r = await fetch('/api/machines');
        if (r.ok) {
            const data = await r.json();
            _machinesCache = Array.isArray(data) ? data : (data.machines || []);
        }
    } catch (_) { /* ignore */ }
    return _machinesCache;
}

function _machinesForLine(lineId: string): MachineRow[] {
    return _machinesCache.filter(m => {
        const line = (m.line || '').trim();
        if (!line) return false;
        const parts = line.split(',').map(s => s.trim());
        return parts.includes(lineId);
    });
}

function _adminToast(msg: string, isErr = false) {
    const el = document.getElementById('collectors-admin-toast');
    if (!el) return;
    if (msg) {
        const icon = isErr ? '❌' : '✅';
        const color = isErr ? '#f87171' : '#10b981';
        el.innerHTML = `<span style="display:inline-block;padding:.4rem .8rem;background:${color}22;border:1px solid ${color};color:${color};border-radius:6px;font-weight:600">${icon} ${_esc(msg)}</span>`;
        setTimeout(() => {
            if (el.innerHTML.includes(msg)) el.innerHTML = '';
        }, 6000);
    } else {
        el.innerHTML = '';
    }
}

function _flashRow(lineId: string, isErr = false) {
    // Visual confirmation: brief background pulse on the saved row.
    const row = document.querySelector(`tr[data-line="${lineId}"]`) as HTMLElement | null;
    if (!row) return;
    const color = isErr ? 'rgba(248, 113, 113, 0.3)' : 'rgba(16, 185, 129, 0.3)';
    const oldBg = row.style.backgroundColor;
    row.style.transition = 'background-color 0.4s ease';
    row.style.backgroundColor = color;
    setTimeout(() => {
        row.style.backgroundColor = oldBg;
        setTimeout(() => { row.style.transition = ''; }, 500);
    }, 1500);
}

// Per-line debounce timers for auto-save after the user stops editing.
const _gatingDirtyTimers: Record<string, ReturnType<typeof setTimeout>> = {};
const _AUTOSAVE_DELAY_MS = 700;

function _setRowDirty(lineId: string, dirty: boolean) {
    const row = document.querySelector(`tr[data-line="${lineId}"]`) as HTMLElement | null;
    if (!row) return;
    const badge = row.querySelector('.dirty-badge') as HTMLElement | null;
    const btn = row.querySelector('.save-btn') as HTMLElement | null;
    if (dirty) {
        row.style.backgroundColor = 'rgba(245, 158, 11, 0.10)'; // soft amber
        if (badge) badge.style.display = '';
        if (btn) {
            btn.style.boxShadow = '0 0 0 2px rgba(245, 158, 11, 0.6)';
            btn.style.animation = 'pulse-save 1.2s ease-in-out infinite';
        }
    } else {
        row.style.backgroundColor = '';
        if (badge) badge.style.display = 'none';
        if (btn) {
            btn.style.boxShadow = '';
            btn.style.animation = '';
        }
    }
}

export function _collectorsMarkDirty(lineId: string): void {
    _setRowDirty(lineId, true);
    // Debounce: cancel previous timer, schedule a new auto-save.
    if (_gatingDirtyTimers[lineId]) clearTimeout(_gatingDirtyTimers[lineId]);
    _gatingDirtyTimers[lineId] = setTimeout(() => {
        delete _gatingDirtyTimers[lineId];
        _collectorsSaveGating(lineId, /*silent=*/true);
    }, _AUTOSAVE_DELAY_MS);
}

async function _renderKeysTable(keys: ApiKeyRow[], statuses: LineStatusRow[]) {
    const tbody = document.getElementById('collectors-keys-tbody');
    if (!tbody) return;

    const statusByLine = new Map(statuses.map(s => [s.id, s]));
    const rows: string[] = [];

    // Sort: lines first by name, then global keys.
    const lineKeys = keys.filter(k => k.machine_filter && k.machine_filter !== '*');
    const globalKeys = keys.filter(k => !k.machine_filter || k.machine_filter === '*');
    lineKeys.sort((a, b) => (a.machine_filter || '').localeCompare(b.machine_filter || ''));

    for (const k of lineKeys) {
        const lineId = (k.machine_filter || '').split(',')[0].trim();
        const st = statusByLine.get(lineId);
        const status = st?.collector_status || 'unknown';
        const dot = STATUS_DOTS[status];
        const power = st && st.power_kw !== null ? `${st.power_kw.toFixed(2)} kW` : '—';
        const heartbeat = _ago(st?.collector_last_seen || k.last_used);
        const dataReading = _ago(st?.last_sensor_reading_at || null);

        // Diagnostic message: explains exactly WHY the line is yellow/red.
        let diagnostic = '';
        let diagColor = 'var(--text-muted)';
        if (status === 'yellow') {
            const dataAge = st?.data_age_sec;
            if (dataAge === null || dataAge === undefined) {
                diagnostic = 'Conectat dar nu citește (0 date)';
            } else if (dataAge > 120) {
                diagnostic = `Conectat dar date vechi (${_humanSec(dataAge)})`;
            } else {
                diagnostic = 'Heartbeat încetinit';
            }
            diagColor = '#f59e0b';
        } else if (status === 'red') {
            diagnostic = 'Colector oprit (fără heartbeat)';
            diagColor = '#ef4444';
        } else if (status === 'unknown') {
            diagnostic = 'Niciodată conectat';
        }

        const dataReadingStyle = (st?.data_age_sec !== undefined && st?.data_age_sec !== null && st.data_age_sec > 120)
            ? 'color:#f59e0b;font-weight:600' : 'color:var(--text-muted)';

        rows.push(`
            <tr style="border-top:1px solid var(--border)">
                <td style="padding:.5rem .7rem"><strong>${_esc(lineId)}</strong>
                    <div style="font-size:.7rem;color:var(--text-muted)">${_esc(st?.product || '')}</div>
                </td>
                <td style="padding:.5rem .7rem;font-family:monospace;font-size:.75rem">${_esc(k.name)}</td>
                <td style="padding:.5rem .7rem">
                    <div title="Heartbeat colector — ultimul semnal de viață">${_esc(heartbeat)}</div>
                    <div style="font-size:.7rem;${dataReadingStyle};margin-top:.15rem" title="Ultima citire de senzor stocată în DB">📊 ${_esc(dataReading)}</div>
                    ${diagnostic ? `<div style="font-size:.65rem;color:${diagColor};margin-top:.15rem;font-style:italic">${_esc(diagnostic)}</div>` : ''}
                </td>
                <td style="padding:.5rem .7rem">${_esc(power)}</td>
                <td style="padding:.5rem .7rem;font-size:1.1rem" title="${_esc(status)} — heartbeat ${_esc(st?.collector_hb_age_sec ?? '?')}s, date ${_esc(st?.data_age_sec ?? '?')}s">${dot}</td>
                <td style="padding:.5rem .7rem">
                    <button class="btn btn-xs" type="button" onclick="_collectorsRotateKey(${k.id})">Rotește</button>
                </td>
            </tr>
        `);
    }
    if (globalKeys.length) {
        rows.push(`<tr><td colspan="6" style="padding:.5rem .7rem;background:var(--bg);font-size:.75rem;color:var(--text-muted)">Chei globale (admin):</td></tr>`);
        for (const k of globalKeys) {
            rows.push(`
                <tr style="border-top:1px solid var(--border);opacity:.85">
                    <td style="padding:.5rem .7rem">*</td>
                    <td style="padding:.5rem .7rem;font-family:monospace;font-size:.75rem">${_esc(k.name)}</td>
                    <td style="padding:.5rem .7rem">${_esc(_ago(k.last_used))}</td>
                    <td style="padding:.5rem .7rem">—</td>
                    <td style="padding:.5rem .7rem">—</td>
                    <td style="padding:.5rem .7rem">
                        <button class="btn btn-xs" type="button" onclick="_collectorsRotateKey(${k.id})">Rotește</button>
                    </td>
                </tr>
            `);
        }
    }
    if (rows.length === 0) {
        rows.push(`<tr><td colspan="6" style="padding:1rem;text-align:center;color:var(--text-muted)">Nicio cheie configurată</td></tr>`);
    }
    tbody.innerHTML = rows.join('');
}

async function _renderGatingTable(statuses: LineStatusRow[]) {
    const tbody = document.getElementById('collectors-gating-tbody');
    if (!tbody) return;
    await _ensureMachinesCache();

    const rows: string[] = [];
    for (const s of statuses) {
        const isUtility = s.group_type === 'utility';
        const machines = _machinesForLine(s.id);
        // Build select options for decision meter (only machines on this line that have a meter).
        // Without per-machine "has_meter" flag, we just allow all machines on the line; UX hint = test in ciclu.
        const meterOptions = machines.map(m =>
            `<option value="${_esc(m.id)}" ${s.power_decision_machine_id === m.id ? 'selected' : ''}>${_esc(m.id)}${m.name ? ' — ' + _esc(m.name) : ''}</option>`
        ).join('');
        const stateLabel = s.running_state === 'running' ? '🟢 RUN'
            : s.running_state === 'stopped' ? '🔴 STOP'
            : '⚪ ?';
        const powerStr = s.power_kw !== null ? `${s.power_kw.toFixed(2)} kW` : '—';

        if (isUtility) {
            rows.push(`
                <tr style="border-top:1px solid var(--border);opacity:.6">
                    <td style="padding:.5rem .7rem"><strong>${_esc(s.id)}</strong></td>
                    <td style="padding:.5rem .7rem;font-style:italic;color:var(--text-muted)">N/A (utility)</td>
                    <td style="padding:.5rem .7rem">—</td>
                    <td style="padding:.5rem .7rem">—</td>
                    <td style="padding:.5rem .7rem">${_esc(powerStr)}</td>
                    <td style="padding:.5rem .7rem">—</td>
                    <td style="padding:.5rem .7rem"></td>
                </tr>
            `);
            continue;
        }

        const lid = _esc(s.id);
        const dirtyHandler = `_collectorsMarkDirty('${lid}')`;
        rows.push(`
            <tr style="border-top:1px solid var(--border);transition:background-color .3s ease" data-line="${lid}">
                <td style="padding:.5rem .7rem"><strong>${lid}</strong>
                    <div style="font-size:.7rem;color:var(--text-muted)">${_esc(s.product)}</div>
                    <div class="dirty-badge" style="display:none;font-size:.7rem;color:#f59e0b;font-weight:600;margin-top:.2rem">⚠ nesalvat</div>
                </td>
                <td style="padding:.5rem .7rem">
                    <input type="checkbox" id="gating-en-${lid}" ${s.power_gating_enabled ? 'checked' : ''}
                           onchange="${dirtyHandler}" />
                </td>
                <td style="padding:.5rem .7rem">
                    <input type="number" step="0.05" min="0" id="gating-thr-${lid}"
                           value="${s.power_threshold_kw ?? 0.2}" style="width:70px"
                           oninput="${dirtyHandler}" />
                </td>
                <td style="padding:.5rem .7rem">
                    <select id="gating-mid-${lid}" style="min-width:140px" onchange="${dirtyHandler}">
                        <option value="">— alege —</option>
                        ${meterOptions}
                    </select>
                </td>
                <td style="padding:.5rem .7rem">${_esc(powerStr)}</td>
                <td style="padding:.5rem .7rem">${stateLabel}</td>
                <td style="padding:.5rem .7rem">
                    <button class="btn btn-xs btn-green save-btn" type="button" onclick="_collectorsSaveGating('${lid}')">Salvează</button>
                </td>
            </tr>
        `);
    }
    if (rows.length === 0) {
        rows.push(`<tr><td colspan="7" style="padding:1rem;text-align:center;color:var(--text-muted)">Nicio linie</td></tr>`);
    }
    tbody.innerHTML = rows.join('');
}

export async function loadCollectorsAdmin(): Promise<void> {
    _adminToast('');
    try {
        const [keysR, statusR] = await Promise.all([
            fetch('/api/api_keys'),
            fetch('/api/lines/status'),
        ]);
        if (!keysR.ok) throw new Error(`Chei API: HTTP ${keysR.status}`);
        if (!statusR.ok) throw new Error(`Status linii: HTTP ${statusR.status}`);
        const keys: ApiKeyRow[] = await keysR.json();
        const statusData = await statusR.json();
        const statuses: LineStatusRow[] = statusData.lines || [];

        await _renderKeysTable(keys.filter(k => k.active), statuses);
        await _renderGatingTable(statuses);
    } catch (e: any) {
        _adminToast(`Eroare încărcare: ${e?.message || e}`, true);
    }
}

export async function _collectorsRotateKey(keyId: number): Promise<void> {
    if (!await askConfirm(`Rotești cheia API #${keyId}? Colectorul va trebui actualizat cu noua cheie.`)) return;
    try {
        const r = await fetch(`/api/api_keys/${keyId}/rotate`, { method: 'POST' });
        const data = await r.json();
        if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
        const newKey: string = data.api_key;
        // Show key in a prompt so the master can copy it.
        prompt('Noua cheie API (copiaz-o ACUM — nu se mai afișează):', newKey);
        _adminToast(`Cheie #${keyId} rotată. Reconfigurează colectorul cu noua cheie.`);
        await loadCollectorsAdmin();
    } catch (e: any) {
        _adminToast(`Rotire eșuată: ${e?.message || e}`, true);
    }
}

export async function _collectorsSaveGating(lineId: string, silent: boolean = false): Promise<void> {
    // If a debounced auto-save was pending, cancel it (manual click or immediate save wins).
    if (_gatingDirtyTimers[lineId]) {
        clearTimeout(_gatingDirtyTimers[lineId]);
        delete _gatingDirtyTimers[lineId];
    }

    const en = (document.getElementById(`gating-en-${lineId}`) as HTMLInputElement)?.checked || false;
    const thr = parseFloat((document.getElementById(`gating-thr-${lineId}`) as HTMLInputElement)?.value || '0.2');
    const mid = (document.getElementById(`gating-mid-${lineId}`) as HTMLSelectElement)?.value || '';

    if (en && !mid) {
        _adminToast(`Linia ${lineId}: alege contorul deciziv înainte să activezi gating-ul.`, true);
        _flashRow(lineId, true);
        return;
    }
    try {
        const r = await fetch(`/api/config/lines/${encodeURIComponent(lineId)}/power-gating`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                enabled: en ? 1 : 0,
                threshold_kw: thr,
                decision_machine_id: mid || null,
            }),
        });
        const data = await r.json();
        if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
        _setRowDirty(lineId, false);
        _flashRow(lineId, false);
        const detalii = en
            ? `ACTIVAT cu prag ${thr} kW, contor ${mid}`
            : `DEZACTIVAT (prag salvat: ${thr} kW)`;
        const prefix = silent ? '💾 Auto-salvat' : '✅ Salvat';
        _adminToast(`${prefix} — Linia ${lineId}: ${detalii}`);
        // Do NOT reload the whole table here — it destroys focus and un-saved edits on other rows.
        // The in-memory row already reflects what the user selected; server has it persisted.
    } catch (e: any) {
        _adminToast(`Salvare ${lineId} eșuată: ${e?.message || e}`, true);
        _flashRow(lineId, true);
    }
}

export async function _collectorsBuildBundle(): Promise<void> {
    if (!await askConfirm('Generezi un bundle nou de colectoare? Aceasta va REGENERA toate cheile API per linie (vechile chei rămân valide doar până la rotire).', 'După descărcare, dezarhivează pe PC fabrică în D:\\colectoare\\ și rulează setup_tasks.bat.')) return;
    _adminToast('Construiesc bundle… (~30-60s)');
    try {
        const r = await fetch('/api/collectors/bundle', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ regenerate_keys: true }),
        });
        const data = await r.json();
        if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
        _adminToast(`Bundle gata (${data.size_kb} KB). Descărcare…`);
        // Trigger download.
        window.location.href = data.download_url;
    } catch (e: any) {
        _adminToast(`Build eșuat: ${e?.message || e}`, true);
    }
}

/* ═════════════ Card Status Linii (dashboard) ═════════════ */

function _renderLinesStatusCard(statuses: LineStatusRow[]) {
    const grid = document.getElementById('lines-status-grid');
    const upd = document.getElementById('lines-status-updated');
    if (!grid) return;

    // P3.1: card extins (280px+) cu info complet despre linie + heartbeat colector + freshness date.
    // P3.2: click pe card → window.openLineLiveDetail(lineId) (modal informativ read-only).
    const cards: string[] = statuses.map(s => {
        const dot = STATUS_DOTS[s.collector_status];
        const powerStr = s.power_kw !== null ? s.power_kw.toFixed(2) : '—';
        const powerCls = s.power_kw !== null && s.power_kw > 1 ? 'lsc-pwr-on' : 'lsc-pwr-off';

        const stateBadge = s.running_state === 'running'
            ? '<span class="lsc-state lsc-state-run">▶ PRODUCȚIE</span>'
            : s.running_state === 'stopped'
                ? '<span class="lsc-state lsc-state-stop">■ OPRITĂ</span>'
                : '<span class="lsc-state lsc-state-unk">? necunoscut</span>';

        // Heartbeat colector (cât de proaspăt e canalul de date)
        const hbStr = s.collector_hb_age_sec !== null
            ? (s.collector_hb_age_sec < 60 ? `${s.collector_hb_age_sec}s` : `${Math.floor(s.collector_hb_age_sec / 60)}min`)
            : '—';
        const hbCls = s.collector_hb_age_sec === null ? 'lsc-hb-none'
            : s.collector_hb_age_sec < 60 ? 'lsc-hb-fresh'
                : s.collector_hb_age_sec < 300 ? 'lsc-hb-stale'
                    : 'lsc-hb-dead';

        // Vârsta ultimei citiri reale de senzor (poate fi diferită de heartbeat — colectorul
        // poate fi viu dar fără date de la PLC/meter)
        const dataStr = s.data_age_sec !== null
            ? (s.data_age_sec < 60 ? `${s.data_age_sec}s`
                : s.data_age_sec < 3600 ? `${Math.floor(s.data_age_sec / 60)}min`
                    : `${Math.floor(s.data_age_sec / 3600)}h`)
            : '—';

        const utilBadge = s.group_type === 'utility'
            ? '<span class="lsc-util-badge">UTIL</span>'
            : '';

        // Indicator dacă power gating e activ pe linie
        const gatingBadge = s.power_gating_enabled
            ? `<span class="lsc-gating-badge" title="Power gating activ — colectorul oprește citirile când consum sub ${s.power_threshold_kw ?? '?'} kW">⚡gate</span>`
            : '';

        return `
            <div class="lsc-card" data-line-id="${_esc(s.id)}" tabindex="0" role="button" aria-label="Detalii live linia ${_esc(s.id)}">
                <div class="lsc-head">
                    <div class="lsc-id">
                        <span class="lsc-icon">${_esc(s.icon || '⚙')}</span>
                        <span class="lsc-name">${_esc(s.id)}</span>
                        ${utilBadge}
                    </div>
                    <div class="lsc-dot" title="Colector: ${_esc(s.collector_status)} (${_esc(hbStr)} în urmă)">${dot}</div>
                </div>
                <div class="lsc-product" title="${_esc(s.product || '')}">${_esc(s.product || '—')}</div>
                <div class="lsc-power-row">
                    <span class="lsc-power ${powerCls}">${_esc(powerStr)}<span class="lsc-power-unit">kW</span></span>
                    ${stateBadge}
                </div>
                <div class="lsc-meta">
                    <span class="lsc-meta-pill ${hbCls}" title="Ultimul heartbeat colector">📡 ${_esc(hbStr)}</span>
                    <span class="lsc-meta-pill" title="Vârsta ultimei citiri de senzor">🕓 ${_esc(dataStr)}</span>
                    ${gatingBadge}
                </div>
            </div>
        `;
    });
    grid.innerHTML = cards.join('') || `<div style="grid-column:1/-1;color:var(--text-muted);text-align:center;font-size:.85rem;padding:.5rem">Nicio linie configurată</div>`;
    if (upd) upd.textContent = `actualizat ${new Date().toLocaleTimeString()}`;

    // Click handler pe fiecare card → openLineLiveDetail(lineId)
    // Folosim event delegation pentru robustețe (re-render la fiecare 10s).
    grid.querySelectorAll('.lsc-card').forEach(card => {
        const handler = () => {
            const lineId = (card as HTMLElement).dataset.lineId;
            if (!lineId) return;
            const fn = (window as any).openLineLiveDetail;
            if (typeof fn === 'function') {
                fn(lineId);
            } else {
                console.warn('openLineLiveDetail not available; click ignored for', lineId);
            }
        };
        card.addEventListener('click', handler);
        card.addEventListener('keydown', (e: any) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handler();
            }
        });
    });
}

let _linesStatusTimer: any = null;

export async function loadLinesStatusCard(): Promise<void> {
    try {
        const r = await fetch('/api/lines/status');
        if (!r.ok) return;
        const data = await r.json();
        _renderLinesStatusCard(data.lines || []);
    } catch (_) { /* silent */ }
}

export function startLinesStatusAutoRefresh(intervalMs = 10000): void {
    if (_linesStatusTimer) clearInterval(_linesStatusTimer);
    loadLinesStatusCard();
    _linesStatusTimer = setInterval(loadLinesStatusCard, intervalMs);
}

export function stopLinesStatusAutoRefresh(): void {
    if (_linesStatusTimer) {
        clearInterval(_linesStatusTimer);
        _linesStatusTimer = null;
    }
}

/* ═════════════ Wire up listeners on DOMContentLoaded ═════════════ */

function _wireButtons() {
    const refreshBtn = document.getElementById('btn-collectors-refresh');
    if (refreshBtn) {
        refreshBtn.addEventListener('click', () => { loadCollectorsAdmin(); });
    }
    const bundleBtn = document.getElementById('btn-collectors-bundle');
    if (bundleBtn) {
        bundleBtn.addEventListener('click', () => { _collectorsBuildBundle(); });
    }
}

if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            _wireButtons();
            startLinesStatusAutoRefresh(10000);
        });
    } else {
        _wireButtons();
        startLinesStatusAutoRefresh(10000);
    }
}
